import { useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  InputAdornment,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AppRoutes } from "../navigation/AppRoutes";
import { useColorScheme } from "../context/ColorSchemeContext";
import { useI18n } from "../context/I18nContext";
import { isAdminUser, isTeacherUser } from "../model/Functions";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import { topBarHeight } from "../constants/layout";
import { surfaceActionIconButtonSx } from "../theme/surfaceActionIconButtonSx";
import logoBlack from "../assets/logo-black.png";
import logoWhite from "../assets/logo-white.png";

const drawerWidth = 280;

export function DocTopBar() {
  const muiTheme = useTheme();
  const s = muiTheme.palette.saas;
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const nav = useNavigate();
  const { pathname } = useLocation();
  // Onglet actif : surligné dans la barre de menu (remplace le bandeau titre de page).
  const isActive = (route: string) => pathname === route || pathname.startsWith(route + "/");
  const navButtonSx = (route: string) => ({
    color: s.topBarText,
    borderRadius: 1.5,
    px: 1.5,
    fontWeight: isActive(route) ? 700 : 500,
    backgroundColor: isActive(route) ? alpha(s.accentBlue, 0.16) : "transparent",
    "&:hover": {
      backgroundColor: isActive(route) ? alpha(s.accentBlue, 0.24) : alpha(s.topBarText, 0.08),
    },
  });
  const { scheme, toggleScheme } = useColorScheme();
  const brandLogo = scheme === "dark" ? logoWhite : logoBlack;
  const { language, setLanguage, t } = useI18n();

  const closeMobile = () => setMobileOpen(false);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = search.trim();
    nav(q ? `${AppRoutes.SEARCH}?q=${encodeURIComponent(q)}` : AppRoutes.SEARCH);
  };

  return (
    <AppBar
      position="sticky"
      color="default"
      elevation={0}
      enableColorOnDark
      sx={{
        height: topBarHeight,
        bgcolor: s.topBarBg,
        backgroundImage: "none",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderBottom: 1,
        borderColor: s.topBarBorder,
      }}
    >
      <Toolbar
        component="header"
        sx={{ gap: 1, minHeight: topBarHeight, boxSizing: "border-box" }}
      >
        {isMobile && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            aria-label={t("nav.openMenu")}
            sx={{ color: s.topBarText, flexShrink: 0 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Typography
          component={Link}
          to={AppRoutes.CATALOG}
          variant="h6"
          onClick={closeMobile}
          sx={{
            color: s.topBarText,
            textDecoration: "none",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            flex: isMobile ? 1 : undefined,
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={brandLogo}
            alt=""
            sx={{
              height: 28,
              width: "auto",
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {t("topbar.brand")}
          </Box>
        </Typography>
        {!isMobile && (
          <Box component="nav" sx={{ flex: 1, display: "flex", gap: 1, ml: 3 }} aria-label="Main">
            <Button
              component={Link}
              to={AppRoutes.CATALOG}
              aria-current={isActive(AppRoutes.CATALOG) ? "page" : undefined}
              sx={navButtonSx(AppRoutes.CATALOG)}
            >
              {t("nav.catalog")}
            </Button>
            {(isAdminUser() || isTeacherUser()) && (
              <Button
                component={Link}
                to={AppRoutes.NEW}
                aria-current={isActive(AppRoutes.NEW) ? "page" : undefined}
                sx={navButtonSx(AppRoutes.NEW)}
              >
                {t("nav.addDoc")}
              </Button>
            )}
          </Box>
        )}
        {!isMobile && (
          <Box
            component="form"
            onSubmit={submitSearch}
            sx={{ flexShrink: 0, mr: 1 }}
          >
            <TextField
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("search.placeholder")}
              aria-label={t("search.placeholder")}
              sx={{ width: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: s.topBarTextMuted }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}
        {!isMobile && (
          <>
            <Button
              size="small"
              sx={{ color: s.topBarTextMuted, minWidth: 52 }}
              onClick={() => setLanguage(language === "fr" ? "en" : "fr")}
            >
              {language.toUpperCase()}
            </Button>
            <IconButton
              onClick={toggleScheme}
              aria-label="theme"
              size="small"
              sx={surfaceActionIconButtonSx(muiTheme, "primary")}
            >
              {scheme === "dark" ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </IconButton>
          </>
        )}
      </Toolbar>
      <Drawer
        variant="temporary"
        anchor="right"
        open={isMobile && mobileOpen}
        onClose={closeMobile}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: drawerWidth, boxSizing: "border-box" } }}
        sx={{ display: { md: "none" } }}
      >
        <Box
          role="navigation"
          aria-label="Main"
          sx={{ pt: "50px", pb: 1, display: "flex", flexDirection: "column", minHeight: "100%" }}
        >
          <List dense component="nav" disablePadding>
            <ListItemButton
              component={Link}
              to={AppRoutes.CATALOG}
              selected={isActive(AppRoutes.CATALOG)}
              onClick={closeMobile}
            >
              <ListItemText primary={t("nav.catalog")} />
            </ListItemButton>
            <ListItemButton
              component={Link}
              to={AppRoutes.SEARCH}
              selected={isActive(AppRoutes.SEARCH)}
              onClick={closeMobile}
            >
              <ListItemText primary={t("nav.search")} />
            </ListItemButton>
            {(isAdminUser() || isTeacherUser()) && (
              <ListItemButton
                component={Link}
                to={AppRoutes.NEW}
                selected={isActive(AppRoutes.NEW)}
                onClick={closeMobile}
              >
                <ListItemText primary={t("nav.addDoc")} />
              </ListItemButton>
            )}
          </List>
          <Divider sx={{ my: 1 }} />
          <List dense disablePadding>
            <ListItemButton
              onClick={() => {
                setLanguage(language === "fr" ? "en" : "fr");
              }}
            >
              <ListItemText
                primary={t("viewer.language")}
                secondary={language === "fr" ? "EN" : "FR"}
              />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                toggleScheme();
              }}
            >
              <ListItemText
                primary={t("nav.theme")}
                secondary={scheme === "dark" ? t("nav.themeDark") : t("nav.themeLight")}
              />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}
